import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { ChatResponse } from '@/types';
import { getAuthToken, handleUnauthorized } from '@/lib/auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL + '/api/v1';

export type ChatSessionSummary = {
  session_id: string;
  started_at: string;
  last_message_at: string;
  message_count: number;
  title: string;
};

export type ChatHistoryMessage = {
  role: 'user' | 'assistant';
  message: string;
};

export type ChatFeedback = {
  session_id: string;
  message_id: string;
  response_text: string;
  rating: 'helpful' | 'not_helpful';
  publicMode?: boolean;
};

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (headers) => {
    headers.set('Content-Type', 'application/json');
    const token = getAuthToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

export const chatApi = createApi({
  reducerPath: 'chatApi',
  baseQuery: async (args, api, extraOptions) => {
    const result = await rawBaseQuery(args, api, extraOptions);
    if ('error' in result && result.error) {
      const status = (result.error as { status?: number }).status;
      if (status === 401) {
        handleUnauthorized();
      }
    }
    return result;
  },
  tagTypes: ['Chat'],
  endpoints: (builder) => ({
    chat: builder.mutation<ChatResponse, { input: string; session_id?: string }>({
      query: ({ input, session_id }) => ({
        url: `chat${session_id ? `?session_id=${session_id}` : ''}`,
        method: 'POST',
        body: { input },
      }),
      transformErrorResponse: (response: any) => {
        return {
          message: response?.data?.error || 'An error occurred while processing your request',
          status: response?.status,
        };
      },
    }),

    getChatSessions: builder.query<{ sessions: ChatSessionSummary[] }, { limit?: number } | void>({
      query: (args) => {
        const limit = args && 'limit' in args ? args.limit : undefined;
        return {
          url: `chat/sessions${limit ? `?limit=${limit}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['Chat'],
    }),

    getChatHistory: builder.query<
      { session_id: string; messages: ChatHistoryMessage[] },
      { session_id: string; limit?: number }
    >({
      query: ({ session_id, limit }) => ({
        url: `chat/history?session_id=${encodeURIComponent(session_id)}${limit ? `&limit=${limit}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['Chat'],
    }),
    deleteChatSession: builder.mutation<{ deleted: boolean }, { session_id: string }>({
      query: ({ session_id }) => ({
        url: `chat/sessions/${encodeURIComponent(session_id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Chat'],
    }),
    submitChatFeedback: builder.mutation<{ saved: boolean }, ChatFeedback>({
      query: ({ publicMode, ...body }) => ({
        url: `${publicMode ? 'public/' : ''}chat/feedback`,
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useChatMutation,
  useGetChatSessionsQuery,
  useLazyGetChatHistoryQuery,
  useDeleteChatSessionMutation,
  useSubmitChatFeedbackMutation,
} = chatApi;
